import { DateRangePicker, Input, MultiSelect, Select, Text, type TypeSelectOption } from '@thefarmersfront/kitchen';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { SearchFilter } from '~/components/common';

import { DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS, OPTION_ALL } from '~/constants';

import type {
  DeliveryAgencyReportFilterOptions,
  DeliveryAgencyReportFilterValues,
  DeliveryAgencyReportFilterValuesStatus,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyReportFilter.module.scss';

const DeliveryAgencyReportFilter = ({
  options,
  values,
  valuesStatus,
  onChange,
  onChangeStatus,
  onSearch,
  onReset,
}: {
  options: DeliveryAgencyReportFilterOptions;
  values: DeliveryAgencyReportFilterValues;
  valuesStatus: DeliveryAgencyReportFilterValuesStatus;
  onChange: (values: Partial<DeliveryAgencyReportFilterValues>) => void;
  onChangeStatus: (values: Partial<DeliveryAgencyReportFilterValuesStatus>) => void;
  onSearch: () => void;
  onReset: () => void;
}) => {
  // 운송장 상태에 '배송실패'(또는 전체)가 포함될 때만 배송 실패 사유 활성화
  const hasDeliveryFailedStatus = values.shippingLabelStatuses.some(
    (status) =>
      status.value === OPTION_ALL.value ||
      status.value === DELIVERY_AGENCY_REPORTED_SHIPPING_LABEL_STATUS.DELIVERY_FAILED
  );

  return (
    <SearchFilter formId="delivery-agency-report-search-form" onSubmit={onSearch} onReset={onReset}>
      <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
        <Text size="small" weight="semi-bold" element="p">
          키워드
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={options.searchKeywordTypes}
            value={values.keywordType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => {
              if (value?.value === values.keywordType?.value) return;
              onChange({ keywordType: value, keyword: '' });
              onChangeStatus({ keyword: 'default' });
            }}
          />
          <Input
            type="text"
            size="small"
            placeholder="입력"
            maxLength={50}
            value={values.keyword}
            status={valuesStatus.keyword}
            onChange={(e) => onChange({ keyword: e.target.value })}
            onBlur={(e) => onChange({ keyword: e.target.value.trim() })}
            onFocus={() => onChangeStatus({ keyword: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-date'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 예정일
        </Text>
        <div className={styles['input-container']}>
          <DateRangePicker
            size="small"
            placeholder={{ start: '시작일', end: '종료일' }}
            minDate={dayjs().subtract(3, 'month').startOf('month').toDate()}
            maxDate={dayjs().endOf('day').toDate()}
            value={values.deliveryDateRange}
            status={valuesStatus.deliveryDateRange}
            onChange={(value) => onChange({ deliveryDateRange: value })}
            onFocus={() => onChangeStatus({ deliveryDateRange: 'default' })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-shipping-label-status'])}>
        <Text size="small" weight="semi-bold" element="p">
          운송장 상태
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            options={[OPTION_ALL, ...options.shippingLabelStatuses]}
            value={values.shippingLabelStatuses}
            allOption={OPTION_ALL}
            onChange={(_, value) =>
              onChange({ shippingLabelStatuses: value ?? [], deliveryFailureReasonCategories: [OPTION_ALL] })
            }
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-failure-reason-category'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 실패 사유
        </Text>
        <div className={styles['input-container']}>
          <MultiSelect
            size="small"
            placeholder="선택 (복수 선택 가능)"
            disabled={!hasDeliveryFailedStatus}
            options={[OPTION_ALL, ...options.deliveryFailureReasonCategories]}
            value={values.deliveryFailureReasonCategories}
            allOption={OPTION_ALL}
            onChange={(_, value) => onChange({ deliveryFailureReasonCategories: value ?? [] })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-type'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 유형
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...options.deliveryTypes]}
            value={values.deliveryType}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ deliveryType: value })}
          />
        </div>
      </div>
      <div className={clsx(styles['filter-container'], styles['filter-delivery-result'])}>
        <Text size="small" weight="semi-bold" element="p">
          배송 결과
        </Text>
        <div className={styles['input-container']}>
          <Select
            size="small"
            placeholder="선택"
            options={[OPTION_ALL, ...options.deliveryResults]}
            value={values.deliveryResult}
            onChange={(_: unknown, value: TypeSelectOption<string>) => onChange({ deliveryResult: value })}
          />
        </div>
      </div>
    </SearchFilter>
  );
};

export default DeliveryAgencyReportFilter;
